// Derives an embeddable player URL from a resource's plain YouTube link
// (issue #288). Anything that isn't a recognisable YouTube video link is
// treated as non-embeddable (null) rather than guessed at.

const YOUTUBE_HOSTS = ['youtube.com', 'www.youtube.com', 'm.youtube.com']

// Path prefixes whose next segment is the video id: youtube.com/shorts/<id>,
// /live/<id> (livestream permalinks, common for recorded webinars), /embed/<id>.
const VIDEO_PATH_PREFIXES = ['/shorts/', '/live/', '/embed/']

// YouTube video ids are exactly 11 URL-safe characters. searchParams.get
// percent-decodes, so without this check a crafted v param could smuggle an
// arbitrary path/query into the embed URL.
const VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/

const videoIdFromLink = (link) => {
    let url
    try {
        url = new URL(link)
    } catch {
        return null
    }
    if (url.hostname === 'youtu.be') {
        const [id, ...rest] = url.pathname.slice(1).split('/')
        return rest.length === 0 ? id : null
    }
    if (YOUTUBE_HOSTS.includes(url.hostname)) {
        if (url.pathname === '/watch') return url.searchParams.get('v')
        const prefix = VIDEO_PATH_PREFIXES.find(p => url.pathname.startsWith(p))
        if (prefix) return url.pathname.split('/')[2]
    }
    return null
}

/**
 * Returns `{ videoId, embedUrl, posterUrl }` for a YouTube video link
 * (`watch?v=`, `youtu.be/`, `shorts/`, `live/`, `embed/`), or `null` for
 * anything else. The embed URL uses the privacy-enhanced
 * youtube-nocookie.com host; the poster is YouTube's static thumbnail, so
 * rendering it costs no player request.
 */
export const youtubeEmbed = (link) => {
    if (!link) return null
    const videoId = videoIdFromLink(link)
    if (!videoId || !VIDEO_ID_PATTERN.test(videoId)) return null
    return {
        videoId,
        embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}`,
        posterUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    }
}
